import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, ShoppingCart, Repeat, DollarSign, Package, TrendingUp,
  ArrowUpRight, ArrowDownRight, Loader2, AlertTriangle, Info,
  CheckCircle, ChevronDown, ChevronUp, ArrowRight, X,
  BarChart3, Clock, Star, Heart, ExternalLink, RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtSlug = (s) => s ? s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';
const fmtCur = (v, cur) => cur === 'nok' ? `${v.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})} kr` : `$${v.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}`;

/* ─── Insight Card ─── */
const InsightCard = ({ insight }) => {
  const cfg = { positive: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' }, warning: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' }, info: { icon: Info, color: 'text-stone-500', bg: 'bg-stone-50', border: 'border-stone-100' } }[insight.type] || { icon: Info, color: 'text-stone-500', bg: 'bg-stone-50', border: 'border-stone-100' };
  const Icon = cfg.icon;
  return (
    <div data-testid={`rp-insight-${insight.type}`} className={`flex gap-3 p-3 rounded-lg border ${cfg.bg} ${cfg.border}`}>
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${cfg.color}`} />
      <div><p className="text-xs font-semibold text-stone-700">{insight.title}</p><p className="text-[11px] text-stone-500 mt-0.5">{insight.detail}</p></div>
    </div>
  );
};

/* ─── Section Heading ─── */
const SH = ({ children, testId }) => <h3 data-testid={testId} className="text-sm font-sans font-bold text-stone-600 uppercase tracking-wider mb-3">{children}</h3>;

/* ─── Empty Section ─── */
const Empty = ({ msg }) => (
  <div className="flex flex-col items-center justify-center py-8 text-center">
    <div className="w-10 h-10 rounded-full bg-stone-50 flex items-center justify-center mb-3"><Package className="w-4 h-4 text-stone-300" /></div>
    <p className="text-xs text-stone-400">{msg}</p>
  </div>
);

/* ─── Customer Detail Modal ─── */
const CustomerDetailModal = ({ userId, onClose }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/repeat-purchases/customer/${userId}`);
        setData(res.data);
      } catch { /* noop */ }
      setLoading(false);
    })();
  }, [userId]);

  if (loading) return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-xl p-8" onClick={e => e.stopPropagation()}><Loader2 className="w-5 h-5 animate-spin text-stone-400" /></div>
    </div>
  );

  if (!data) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="customer-detail-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-stone-100 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-sm font-serif font-bold text-stone-800">{data.email}</h3>
            <p className="text-[10px] text-stone-400 mt-0.5">
              {data.total_orders} orders · {data.unique_products} products · {data.is_repeat_buyer ? 'Repeat buyer' : 'One-time buyer'}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-stone-50" data-testid="close-customer-modal"><X className="w-4 h-4 text-stone-400" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Revenue summary */}
          <div className="flex gap-3">
            {data.total_revenue.usd > 0 && (
              <div className="bg-stone-50 rounded-lg p-3 flex-1">
                <p className="text-[10px] text-stone-400">Revenue (USD)</p>
                <p className="text-sm font-serif font-bold text-stone-800">{fmtCur(data.total_revenue.usd, 'usd')}</p>
              </div>
            )}
            {data.total_revenue.nok > 0 && (
              <div className="bg-stone-50 rounded-lg p-3 flex-1">
                <p className="text-[10px] text-stone-400">Revenue (NOK)</p>
                <p className="text-sm font-serif font-bold text-stone-800">{fmtCur(data.total_revenue.nok, 'nok')}</p>
              </div>
            )}
          </div>

          <div className="flex gap-4 text-[10px] text-stone-400">
            <span>First: {data.first_purchase}</span>
            <span>Latest: {data.latest_purchase}</span>
          </div>

          {/* Purchase history */}
          <div>
            <p className="text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-2">Purchase History</p>
            <div className="space-y-2">
              {data.history.map((h, i) => (
                <div key={i} data-testid={`purchase-${i}`} className="border border-stone-100 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <button onClick={() => { onClose(); navigate(`/admin/products/${h.product_slug}`); }} className="text-xs font-semibold text-stone-700 hover:text-stone-900 underline decoration-stone-200 hover:decoration-stone-400 transition-colors">
                        {h.product_title || fmtSlug(h.product_slug)}
                      </button>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-stone-400">{h.date}</span>
                        {h.purchase_type === 'bundle' && <span className="text-[9px] bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded-full font-medium">Bundle</span>}
                        {h.is_first_purchase && <span className="text-[9px] bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded-full font-medium">First</span>}
                        {!h.is_first_purchase && <span className="text-[9px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full font-medium">Repeat</span>}
                        {h.coupon_code && <span className="text-[9px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full font-medium">{h.coupon_code}</span>}
                      </div>
                      {h.bundle_products?.length > 0 && (
                        <p className="text-[10px] text-stone-400 mt-1">Includes: {h.bundle_products.map(fmtSlug).join(', ')}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-semibold text-stone-800">{fmtCur(h.amount, h.currency)}</p>
                      <p className="text-[10px] text-stone-400">{h.payment_provider}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════ */
export const RepeatPurchaseAnalytics = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setBlack] = useState('revenue');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const fetchData = useCallback(async (s) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/repeat-purchases?sort=${s}`);
      setData(res.data);
    } catch (err) { console.error('Repeat purchases error:', err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(sort); }, [sort, fetchData]);

  if (loading) return (
    <div className="flex items-center justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-stone-400" /></div>
  );

  if (!data) return (
    <div className="text-center py-10"><p className="text-xs text-stone-400">Failed to load repeat purchase analytics</p></div>
  );

  const { metrics: m, customers, product_next_product: pnp, product_pairs: pairs, cohort, first_purchase: fp, insights } = data;
  const hasData = m.total_customers > 0;
  const hasRepeat = m.repeat_buyers > 0;

  return (
    <div data-testid="repeat-purchase-analytics" className="space-y-8 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-serif font-bold text-stone-800">Repeat Purchase Analytics</h2>
          <p className="text-[11px] text-stone-400 mt-0.5">Customer behavior, cross-sell opportunities, and lifetime value</p>
        </div>
        <button onClick={() => fetchData(sort)} className="p-2 rounded-lg hover:bg-stone-50 text-stone-400 hover:text-stone-600" data-testid="rp-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>

      {/* Global empty state */}
      {!hasData && (
        <div data-testid="rp-empty-state" className="bg-white border border-stone-100 rounded-xl p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-stone-50 flex items-center justify-center mx-auto mb-4"><ShoppingCart className="w-6 h-6 text-stone-300" /></div>
          <h3 className="font-serif text-base font-bold text-stone-700 mb-1">No completed purchases yet</h3>
          <p className="text-xs text-stone-400 max-w-sm mx-auto">Customer analytics will populate as customers make paid purchases. Only completed/paid transactions are counted.</p>
        </div>
      )}

      {hasData && (
        <>
          {/* KPI Overview */}
          <SH testId="rp-kpi-heading">Customer Metrics</SH>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
            <div data-testid="rp-total-customers" className="bg-white border border-stone-100 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1"><Users className="w-3 h-3 text-stone-400" /><span className="text-[10px] text-stone-400 uppercase tracking-wider">Customers</span></div>
              <span className="text-base font-serif font-bold text-stone-800">{m.total_customers}</span>
            </div>
            <div data-testid="rp-one-time" className="bg-white border border-stone-100 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1"><ShoppingCart className="w-3 h-3 text-stone-400" /><span className="text-[10px] text-stone-400 uppercase tracking-wider">One-Time</span></div>
              <span className="text-base font-serif font-bold text-stone-800">{m.one_time}</span>
            </div>
            <div data-testid="rp-repeat-buyers" className="bg-white border border-stone-100 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1"><Repeat className="w-3 h-3 text-stone-400" /><span className="text-[10px] text-stone-400 uppercase tracking-wider">Repeat</span></div>
              <span className="text-base font-serif font-bold text-stone-800">{m.repeat_buyers}</span>
              <p className="text-[10px] text-stone-400 mt-0.5">{m.repeat_rate}% rate</p>
            </div>
            <div data-testid="rp-avg-products" className="bg-white border border-stone-100 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1"><Package className="w-3 h-3 text-stone-400" /><span className="text-[10px] text-stone-400 uppercase tracking-wider">Avg Products</span></div>
              <span className="text-base font-serif font-bold text-stone-800">{m.avg_products_per_customer}</span>
            </div>
            <div data-testid="rp-avg-orders" className="bg-white border border-stone-100 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1"><BarChart3 className="w-3 h-3 text-stone-400" /><span className="text-[10px] text-stone-400 uppercase tracking-wider">Avg Orders</span></div>
              <span className="text-base font-serif font-bold text-stone-800">{m.avg_orders_per_customer}</span>
            </div>
          </div>

          {/* Revenue breakdown */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div data-testid="rp-total-rev" className="bg-white border border-stone-100 rounded-xl p-4">
              <div className="flex items-center gap-1.5 mb-1"><DollarSign className="w-3.5 h-3.5 text-stone-400" /><span className="text-[10px] text-stone-400 uppercase tracking-wider">Total Revenue</span></div>
              {m.total_revenue.usd > 0 && <p className="text-sm font-serif font-bold text-stone-800">{fmtCur(m.total_revenue.usd, 'usd')}</p>}
              {m.total_revenue.nok > 0 && <p className="text-xs text-stone-500">{fmtCur(m.total_revenue.nok, 'nok')}</p>}
              {m.total_revenue.usd === 0 && m.total_revenue.nok === 0 && <p className="text-sm font-serif font-bold text-stone-800">$0.00</p>}
            </div>
            <div data-testid="rp-repeat-rev" className="bg-white border border-stone-100 rounded-xl p-4">
              <div className="flex items-center gap-1.5 mb-1"><Repeat className="w-3.5 h-3.5 text-stone-400" /><span className="text-[10px] text-stone-400 uppercase tracking-wider">Repeat Revenue</span></div>
              {m.repeat_revenue.usd > 0 && <p className="text-sm font-serif font-bold text-stone-800">{fmtCur(m.repeat_revenue.usd, 'usd')}</p>}
              {m.repeat_revenue.nok > 0 && <p className="text-xs text-stone-500">{fmtCur(m.repeat_revenue.nok, 'nok')}</p>}
              {m.repeat_revenue.usd === 0 && m.repeat_revenue.nok === 0 && <p className="text-sm font-serif font-bold text-stone-800">$0.00</p>}
              {(m.repeat_revenue_share.usd > 0 || m.repeat_revenue_share.nok > 0) && (
                <p className="text-[10px] text-stone-400 mt-0.5">{m.repeat_revenue_share.usd}% of USD revenue</p>
              )}
            </div>
            <div data-testid="rp-avg-rev" className="bg-white border border-stone-100 rounded-xl p-4">
              <div className="flex items-center gap-1.5 mb-1"><TrendingUp className="w-3.5 h-3.5 text-stone-400" /><span className="text-[10px] text-stone-400 uppercase tracking-wider">Avg per Customer</span></div>
              <p className="text-sm font-serif font-bold text-stone-800">{fmtCur(m.avg_revenue_per_customer.usd, 'usd')}</p>
              {m.avg_revenue_per_customer.nok > 0 && <p className="text-xs text-stone-500">{fmtCur(m.avg_revenue_per_customer.nok, 'nok')}</p>}
            </div>
            <div data-testid="rp-repeat-vs-onetime" className="bg-white border border-stone-100 rounded-xl p-4">
              <div className="flex items-center gap-1.5 mb-1"><Star className="w-3.5 h-3.5 text-stone-400" /><span className="text-[10px] text-stone-400 uppercase tracking-wider">Repeat vs One-time</span></div>
              <p className="text-xs text-stone-600">Repeat: <span className="font-semibold">{fmtCur(m.avg_repeat_value.usd, 'usd')}</span></p>
              <p className="text-xs text-stone-600">One-time: <span className="font-semibold">{fmtCur(m.avg_onetime_value.usd, 'usd')}</span></p>
            </div>
          </div>

          {/* Customer Table */}
          <div className="bg-white border border-stone-100 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <SH testId="rp-customers-heading">Customers</SH>
              <div className="flex gap-1">
                {[{k:'revenue',l:'Revenue'},{k:'orders',l:'Orders'},{k:'products',l:'Products'},{k:'recent',l:'Recent'}].map(s => (
                  <button key={s.k} data-testid={`rp-sort-${s.k}`} onClick={() => setBlack(s.k)}
                    className={`px-2 py-1 text-[10px] font-medium rounded-md border transition-all ${sort === s.k ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-400 border-stone-200 hover:border-stone-300'}`}>
                    {s.l}
                  </button>
                ))}
              </div>
            </div>
            {customers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-stone-100">
                      {['Customer','Orders','Products','Revenue','First','Latest','Span',''].map((h,i) => (
                        <th key={i} className="text-[10px] font-bold text-stone-400 uppercase tracking-wider py-2 pr-3">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c, i) => (
                      <tr key={i} data-testid={`rp-customer-row-${i}`} className="border-b border-stone-50 last:border-0 hover:bg-stone-50/30 transition-colors">
                        <td className="py-2 pr-3">
                          <span className="text-xs font-medium text-stone-700">{c.email}</span>
                          {c.is_repeat && <span className="ml-1.5 text-[9px] bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded-full font-medium">Repeat</span>}
                        </td>
                        <td className="text-xs text-stone-600 py-2 pr-3">{c.orders}</td>
                        <td className="text-xs text-stone-600 py-2 pr-3">{c.unique_products}</td>
                        <td className="text-xs text-stone-600 py-2 pr-3">
                          {c.revenue_usd > 0 && <span>{fmtCur(c.revenue_usd, 'usd')}</span>}
                          {c.revenue_nok > 0 && <span className="block text-[10px] text-stone-400">{fmtCur(c.revenue_nok, 'nok')}</span>}
                        </td>
                        <td className="text-[10px] text-stone-400 py-2 pr-3">{c.first_purchase}</td>
                        <td className="text-[10px] text-stone-400 py-2 pr-3">{c.latest_purchase}</td>
                        <td className="text-[10px] text-stone-400 py-2 pr-3">{c.days_span > 0 ? `${c.days_span}d` : '—'}</td>
                        <td className="py-2">
                          <button data-testid={`rp-view-customer-${i}`} onClick={() => setSelectedCustomer(c.user_id)}
                            className="text-[10px] text-stone-500 hover:text-stone-800 underline decoration-stone-200 hover:decoration-stone-400 transition-colors">
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty msg="No customers with completed purchases" />
            )}
          </div>

          {/* Two columns: Product → Next Product + Product Pairs */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Product → Next Product */}
            <div className="bg-white border border-stone-100 rounded-xl p-5">
              <SH testId="rp-next-product-heading">Product → Next Purchase</SH>
              {pnp.length > 0 ? (
                <div className="space-y-2">
                  {pnp.slice(0, 10).map((item, i) => (
                    <div key={i} data-testid={`rp-pnp-${i}`} className="flex items-center gap-2 py-1.5 border-b border-stone-50 last:border-0">
                      <button onClick={() => navigate(`/admin/products/${item.source}`)} className="text-xs text-stone-700 hover:text-stone-900 underline decoration-stone-200 hover:decoration-stone-400 transition-colors truncate max-w-[120px]">{fmtSlug(item.source)}</button>
                      <ArrowRight className="w-3 h-3 text-stone-300 flex-shrink-0" />
                      <button onClick={() => navigate(`/admin/products/${item.next}`)} className="text-xs text-stone-700 hover:text-stone-900 underline decoration-stone-200 hover:decoration-stone-400 transition-colors truncate max-w-[120px]">{fmtSlug(item.next)}</button>
                      <span className="ml-auto text-[10px] text-stone-400 flex-shrink-0">{item.customers} customers</span>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty msg="No consecutive purchase patterns yet. Requires multiple customers with 2+ orders." />
              )}
            </div>

            {/* Product Pairs */}
            <div className="bg-white border border-stone-100 rounded-xl p-5">
              <SH testId="rp-pairs-heading">Product Pairs (Co-ownership)</SH>
              {pairs.length > 0 ? (
                <div className="space-y-2">
                  {pairs.slice(0, 10).map((pair, i) => (
                    <div key={i} data-testid={`rp-pair-${i}`} className="flex items-center gap-2 py-1.5 border-b border-stone-50 last:border-0">
                      <button onClick={() => navigate(`/admin/products/${pair.product_a}`)} className="text-xs text-stone-700 hover:text-stone-900 underline decoration-stone-200 hover:decoration-stone-400 transition-colors truncate max-w-[120px]">{fmtSlug(pair.product_a)}</button>
                      <span className="text-[10px] text-stone-300">+</span>
                      <button onClick={() => navigate(`/admin/products/${pair.product_b}`)} className="text-xs text-stone-700 hover:text-stone-900 underline decoration-stone-200 hover:decoration-stone-400 transition-colors truncate max-w-[120px]">{fmtSlug(pair.product_b)}</button>
                      <span className="ml-auto text-[10px] text-stone-400 flex-shrink-0">{pair.shared_customers} shared</span>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty msg="No product pairs found. Requires customers who own multiple products." />
              )}
            </div>
          </div>

          {/* Two columns: First Purchase + Time to Second */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* First Purchase Analysis */}
            <div className="bg-white border border-stone-100 rounded-xl p-5">
              <SH testId="rp-first-purchase-heading">Entry Products (First Purchase)</SH>
              {fp.length > 0 ? (
                <div className="space-y-2">
                  {fp.slice(0, 8).map((item, i) => (
                    <div key={i} data-testid={`rp-fp-${i}`} className="flex items-center justify-between py-1.5 border-b border-stone-50 last:border-0">
                      <div>
                        <button onClick={() => navigate(`/admin/products/${item.product}`)} className="text-xs text-stone-700 hover:text-stone-900 underline decoration-stone-200 hover:decoration-stone-400 transition-colors">{fmtSlug(item.product)}</button>
                        <p className="text-[10px] text-stone-400">{item.first_purchases} customers · {item.repeat_rate}% repeat</p>
                      </div>
                      <div className="text-right">
                        {item.later_revenue_usd > 0 && <p className="text-[10px] font-semibold text-stone-600">{fmtCur(item.later_revenue_usd, 'usd')} later</p>}
                        {item.later_revenue_nok > 0 && <p className="text-[10px] text-stone-400">{fmtCur(item.later_revenue_nok, 'nok')} later</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty msg="No first purchase data available" />
              )}
            </div>

            {/* Time to Second Purchase */}
            <div className="bg-white border border-stone-100 rounded-xl p-5">
              <SH testId="rp-cohort-heading">Time to Second Purchase</SH>
              {cohort && cohort.sample_size > 0 ? (
                <div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-stone-50 rounded-lg p-3">
                      <p className="text-[10px] text-stone-400">Average</p>
                      <p className="text-sm font-serif font-bold text-stone-800">{cohort.avg_days} days</p>
                    </div>
                    <div className="bg-stone-50 rounded-lg p-3">
                      <p className="text-[10px] text-stone-400">Median</p>
                      <p className="text-sm font-serif font-bold text-stone-800">{cohort.median_days} days</p>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {[{label: 'Within 7 days', pct: cohort.within_7d}, {label: 'Within 30 days', pct: cohort.within_30d}, {label: 'Within 60 days', pct: cohort.within_60d}, {label: 'Within 90 days', pct: cohort.within_90d}].map((row, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-[10px] text-stone-500 w-24">{row.label}</span>
                        <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                          <div className="h-full bg-stone-700 rounded-full transition-all" style={{ width: `${row.pct}%` }} />
                        </div>
                        <span className="text-[10px] font-semibold text-stone-600 w-10 text-right">{row.pct}%</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-stone-400 mt-2">{cohort.sample_size} repeat customers</p>
                </div>
              ) : (
                <Empty msg="Requires at least 2 repeat customers to calculate time-to-second-purchase." />
              )}
            </div>
          </div>

          {/* Cross-sell Insights */}
          <div>
            <SH testId="rp-insights-heading">Cross-Sell Insights</SH>
            <div className="space-y-2">
              {insights.map((ins, i) => <InsightCard key={i} insight={ins} />)}
            </div>
          </div>

          {/* Footer */}
          <p className="text-[10px] text-stone-400 text-center">
            Only completed/paid purchases. Subscription renewals excluded. NOK/USD shown separately. Multi-product bundle counted as one order.
          </p>
        </>
      )}

      {/* Customer detail modal */}
      {selectedCustomer && <CustomerDetailModal userId={selectedCustomer} onClose={() => setSelectedCustomer(null)} />}
    </div>
  );
};
